import { useState, useEffect } from 'react';
import api from '../services/api';

// Custom hook for managing dynamic content
export const useDynamicContent = (section) => {
    const [content, setContent] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadContent = async () => {
            try {
                setLoading(true);
                const response = await api.getContentBySection(section);
                if (response.success) {
                    setContent(response.data);
                }
            } catch (err) {
                console.error(`Failed to load content for section: ${section}`, err);
                setError(err.message);
                // Fallback to default content
                setContent(getDefaultContent(section));
            } finally {
                setLoading(false);
            }
        };

        loadContent();
    }, [section]);

    return { content, loading, error };
};

// Helper function to get content by key
export const getContentByKey = (content, key, defaultValue = '') => {
    return content[key]?.content || defaultValue;
};

// Helper function to get content with metadata
export const getContentWithMeta = (content, key, defaultValue = {}) => {
    const item = content[key];
    if (!item) return defaultValue;
    
    return {
        content: item.content,
        metadata: item.metadata || {},
        type: item.type
    };
};

// Default content fallbacks
const getDefaultContent = (section) => {
    const defaults = {
        homepage: {
            homepage_hero_title: { content: "India's No.1 Home Catering Platform" },
            homepage_hero_subtitle: { content: "Discover delicious food, professional service, and unforgettable experiences for your special occasions." },
            homepage_hero_image: { content: "/images/hero-catering.jpg", metadata: { alt_text: "Catering service hero image" } },
            homepage_features_title: { content: "Our Services" },
            homepage_features_subtitle: { content: "We provide comprehensive catering solutions designed to make your special moments truly memorable." }
        },
        footer: {
            footer_about_text: { content: "Spoon & Spice is India's premier catering platform, connecting customers with the best local caterers." },
            footer_company_name: { content: "Spoon & Spice" },
            footer_copyright: { content: "© 2025 Spoon & Spice. All rights reserved." }
        },
        contact: {
            contact_phone: { content: "+91 7483423925" },
            contact_email: { content: "hello@spoonandspice.com" },
            contact_address: { content: "123 Business District, Mumbai, Maharashtra 400001" }
        },
        about: {
            about_title: { content: "About Spoon & Spice" },
            about_description: { content: "Founded in 2020, Spoon & Spice has revolutionized the catering industry in India by connecting customers with verified, professional caterers." }
        },
        services: {
            services_title: { content: "Our Catering Services" },
            services_subtitle: { content: "From intimate gatherings to grand celebrations, we provide comprehensive catering solutions for every occasion." }
        },
        seo: {
            seo_homepage_title: { content: "Spoon & Spice - India's No.1 Home Catering Platform" },
            seo_homepage_description: { content: "Discover delicious food, professional service, and unforgettable experiences for your special occasions." },
            seo_keywords: { content: "catering, food delivery, wedding catering, birthday catering, corporate events" }
        }
    };

    return defaults[section] || {};
};

// Hook for SEO content
export const useSEOContent = () => {
    return useDynamicContent('seo');
};

// Hook for homepage content
export const useHomepageContent = () => {
    return useDynamicContent('homepage');
};

// Hook for footer content
export const useFooterContent = () => {
    return useDynamicContent('footer');
};

// Hook for contact content
export const useContactContent = () => {
    return useDynamicContent('contact');
};

// Hook for about content
export const useAboutContent = () => {
    return useDynamicContent('about');
};

// Hook for services content
export const useServicesContent = () => {
    return useDynamicContent('services');
};
